import React, { useState, useRef } from "react";
import { useAtom } from "jotai";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import { ThemeProvider, useTheme, createTheme } from "@mui/material/styles";
import {
    filteredProvidersCountAtom,
    groupedProviderLocationsAtom,
    providerLocationsAtom,
    providerLocationsFilterAtom
} from "./analysisAtoms";
import { Typography } from "@mui/material";

const providersFilterTheme = ({ palette }) =>
    createTheme({
        palette,
        typography: {
            fontSize: 14
        },
        components: {
            MuiList: {
                styleOverrides: {
                    root: {
                        padding: "10px 18px 13px 18px!important",
                        width: "100%"
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: palette.inputBorder.main
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: palette.primary.main
                        }
                    }
                }
            },
            MuiFormControl: {
                styleOverrides: {
                    root: {
                        minWidth: "141px"
                    }
                }
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        margin: "8px 0px"
                    }
                }
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        height: "24px",
                        marginBottom: "0px",
                        "& .MuiButtonBase-root": {
                            padding: "1px 9px !important"
                        }
                    }
                }
            },
            MuiInputLabel: {
                styleOverrides: {
                    shrink: {
                        top: "0px !important"
                    }
                }
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        padding: "0px",
                        height: "33px",
                        backgroundColor: "transparent !important"
                    }
                }
            },
            MuiSelect: {
                styleOverrides: {
                    select: {
                        color: palette.text.main,
                        top: "-7px !important",
                        padding: "8px 14px",
                        width: "auto"
                    },
                    icon: {
                        top: "7px !important"
                    }
                }
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        padding: "0px 15px 0px 0px",
                        marginLeft: "-3px"
                    }
                }
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        width: "24px",
                        height: "24px"
                    },
                    checkbox: {
                        height: "18px",
                        width: "18px"
                    }
                }
            },
            MuiTypography: {
                styleOverrides: {
                    root: {
                        color: palette.text.main
                    },
                    helperText: {
                        fontSize: "0.75rem",
                        fontWeight: "normal",
                        color: palette.helperText.main,
                        fontStyle: "italic",
                        fontFamily: ["Roboto", "sans-serif"].join(",")
                    }
                }
            }
        }
    });

export default function ProviderFilter() {
    const [providerLocationsFilter, setProviderLocationsFilter] = useAtom(
        providerLocationsFilterAtom
    );
    const [allProviderLocations] = useAtom(providerLocationsAtom);
    const [groupedProviderLocations] = useAtom(groupedProviderLocationsAtom);
    const [filteredProvidersCount] = useAtom(filteredProvidersCountAtom);
    const [openProvidersFilter, setopenProvidersFilters] = useState(false);
    const [, setAnchorEl] = useState(null);
    const theme = useTheme();
    const providerFilterRef = useRef(null);

    const handleFilterChange = (event) => {
        const {
            target: { value }
        } = event;
        if (value === "select-all-provider-locations") return;
        if (value === "clear-all-provider-locations") return;
        if (Array.isArray(value)) {
            if (
                value.includes("clear-all-provider-locations") ||
                value.includes("select-all-provider-locations")
            )
                return; // do nothing
        }
        const newProviderLocationFilter =
            typeof value === "string" ? value.split(",") : value;
        setProviderLocationsFilter(newProviderLocationFilter);
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        setopenProvidersFilters(true);
        providerFilterRef.current.classList.add("Mui-focused");
    };

    const handleClose = () => {
        setopenProvidersFilters(false);
        providerFilterRef.current.classList.remove("Mui-focused");
    };

    const handleSelectAll = () => {
        setProviderLocationsFilter(allProviderLocations.map((pl) => pl.id));
    };

    const handleClearAll = () => {
        setProviderLocationsFilter([]);
    };
    const handleProviderLocationToggle = (providerLocationIds) => {
        const found =
            providerLocationsFilter?.length === 0
                ? false
                : providerLocationIds.every((pl) =>
                      providerLocationsFilter.includes(pl)
                  );
        const newSelectedProviderLocations = [...providerLocationsFilter];

        if (!found) {
            newSelectedProviderLocations.push(...providerLocationIds);
        } else {
            for (let selectedLocId of providerLocationIds) {
                newSelectedProviderLocations.splice(
                    newSelectedProviderLocations.indexOf(selectedLocId),
                    1
                );
            }
        }

        setProviderLocationsFilter(newSelectedProviderLocations);
    };
    const handleGroupToggle = (groupKey) => {
        const groupProviderLocations = Object.keys(
            groupedProviderLocations[groupKey]
        ).flatMap(
            (providerName) =>
                groupedProviderLocations[groupKey][providerName].ids
        );
        const allGroupProvidersSelected = groupProviderLocations.every(
            (providerLocationId) =>
                providerLocationsFilter.includes(providerLocationId)
        );

        let newSelectedProviderLocations = [...providerLocationsFilter];

        if (allGroupProvidersSelected) {
            newSelectedProviderLocations = newSelectedProviderLocations.filter(
                (providerLocationId) =>
                    !groupProviderLocations.includes(providerLocationId)
            );
        } else {
            groupProviderLocations.forEach((providerLocationId) => {
                if (
                    !newSelectedProviderLocations.includes(providerLocationId)
                ) {
                    newSelectedProviderLocations.push(providerLocationId);
                }
            });
        }

        setProviderLocationsFilter(newSelectedProviderLocations);
    };

    function isGroupChecked(groupKey) {
        let checked = false;
        for (let providerName of Object.keys(
            groupedProviderLocations[groupKey]
        )) {
            checked = groupedProviderLocations[groupKey][
                providerName
            ].ids.every((providerLocationId) =>
                providerLocationsFilter?.length === 0
                    ? false
                    : providerLocationsFilter.includes(providerLocationId)
            );
        }
        return checked;
    }
    return (
        <ThemeProvider theme={providersFilterTheme(theme)}>
            <FormControl sx={{ width: "280px" }}>
                <Select
                    ref={providerFilterRef}
                    sx={{
                        "& .MuiSelect-select": {
                            fontSize: "14px" // Apply font size to the select's content
                        },
                        display: "inline-block",
                        verticalAlign: "middle",
                        justifyContent: "center",
                        alignItems: "center",
                        maxHeight: "36px !important"
                    }}
                    labelId="analysis-provider-multiple-checkbox-label"
                    id="analysis-provider-multiple-checkbox"
                    multiple
                    value={providerLocationsFilter}
                    onOpen={handleClick}
                    onClose={handleClose}
                    open={openProvidersFilter}
                    onChange={handleFilterChange}
                    displayEmpty
                    renderValue={(selected) => {
                        if (selected.length === 0) {
                            return "Select Providers";
                        }
                        return selected.length > 1
                            ? `${filteredProvidersCount} providers selected`
                            : "1 provider selected";
                    }}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                width: 240,
                                maxHeight: 400
                            }
                        }
                    }}
                >
                    <MenuItem
                        onClick={handleSelectAll}
                        key={"select-all-pl"}
                        value={"select-all-provider-locations"}
                    >
                        <Typography
                            sx={{
                                fontWeight: 600,
                                color: "primary.main",
                                cursor: "pointer"
                            }}
                        >
                            Select all
                        </Typography>
                    </MenuItem>
                    <MenuItem
                        onClick={handleClearAll}
                        key={"clear-all-pl"}
                        value={"clear-all-provider-locations"}
                    >
                        <Typography
                            sx={{
                                fontWeight: 600,
                                color: "primary.main",
                                cursor: "pointer"
                            }}
                        >
                            Clear all
                        </Typography>
                    </MenuItem>
                    <hr />
                    {Object.keys(groupedProviderLocations).map((groupKey) => (
                        <div key={groupKey}>
                            <MenuItem
                                onClick={() => handleGroupToggle(groupKey)}
                            >
                                <Checkbox
                                    checked={isGroupChecked(groupKey)}
                                    indeterminate={
                                        Object.keys(
                                            groupedProviderLocations[groupKey]
                                        )
                                            .flatMap(
                                                (provider) =>
                                                    groupedProviderLocations[
                                                        groupKey
                                                    ][provider].ids
                                            )
                                            .some((providerLocationId) =>
                                                providerLocationsFilter.includes(
                                                    providerLocationId
                                                )
                                            ) &&
                                        !Object.keys(
                                            groupedProviderLocations[groupKey]
                                        )
                                            .flatMap(
                                                (provider) =>
                                                    groupedProviderLocations[
                                                        groupKey
                                                    ][provider].ids
                                            )
                                            .every((providerLocationId) =>
                                                providerLocationsFilter?.length ===
                                                0
                                                    ? false
                                                    : providerLocationsFilter.includes(
                                                          providerLocationId
                                                      )
                                            )
                                    }
                                />
                                <ListItemText
                                    disableTypography
                                    primary={
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textTransform: "upperCase"
                                            }}
                                        >
                                            {groupKey}
                                        </Typography>
                                    }
                                />
                            </MenuItem>
                            {Object.keys(groupedProviderLocations[groupKey])
                                .map((providerName) => {
                                    const pl =
                                        groupedProviderLocations[groupKey][
                                            providerName
                                        ];
                                    return {
                                        ids: pl.ids,
                                        providerName,
                                        emailaddress: pl.emailaddress
                                    };
                                })
                                .map(
                                    (
                                        providerLocation,
                                        index,
                                        providerLocationsArray
                                    ) => (
                                        <MenuItem
                                            key={providerLocation.ids.toString()}
                                            value={providerLocation.ids}
                                            sx={{
                                                mt: index === 0 ? 0.5 : 1,
                                                mb:
                                                    providerLocationsArray.length -
                                                        1 ===
                                                    index
                                                        ? 1.5
                                                        : 0.5,
                                                alignItems: "flex-start"
                                            }}
                                            onClick={() =>
                                                handleProviderLocationToggle(
                                                    providerLocation.ids
                                                )
                                            }
                                        >
                                            <Checkbox
                                                checked={providerLocationsFilter.includes(
                                                    providerLocation.ids[0] //no need to check all ids if zeroeth is there then all ids should be there or we can use array.every
                                                )}
                                                size="small"
                                            />
                                            <ListItemText
                                                disableTypography
                                                primary={
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            lineHeight: 1.2
                                                        }}
                                                    >
                                                        {
                                                            providerLocation.providerName
                                                        }
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Typography
                                                        variant="helperText"
                                                        sx={{
                                                            lineHeight: 1.2
                                                        }}
                                                    >
                                                        {
                                                            providerLocation.emailaddress
                                                        }
                                                    </Typography>
                                                }
                                            />
                                        </MenuItem>
                                    )
                                )}
                        </div>
                    ))}
                </Select>
            </FormControl>
        </ThemeProvider>
    );
}
